import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import buildWeeklyProjectCounts from '../../helpers/dataHelpers/buildWeeklyProjectCounts';
import { WEEKLY_CHART_COLORS } from '../../helpers/visualisationDefaults';
import AngledAxisTick from '../Visualisations/AngledAxisTick';
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../helpers/chartPalette';
import ChartDataTable from '../graphData/ChartDataTable';
import { weeklyTable } from '../../helpers/dataHelpers/chartDataTable';

/**
 * Weekly results bar chart for card detail popovers. The hierarchy `records`
 * produce a per-project stacked chart (a single series on non-portfolio
 * tenants, where every record shares one project). Renders nothing without data.
 *
 * @param {object} props
 * @param {Array<{ Timestamp: any, _projectName?: string }>} [props.records] - Hierarchy records.
 * @param {string} [props.title] - Chart heading, e.g. "Antwoorden per week". Omit when the surrounding section already has one.
 * @param {string} [props.dedupeKey] - Record field to dedupe by (e.g. 'FormID' to count respondents, not docs).
 */
const WeeklyResultsChart = ({ records, title, dedupeKey }) => {
    const weeklyProjectData = useMemo(
        () => (records && records.length ? buildWeeklyProjectCounts(records, dedupeKey) : null),
        [records, dedupeKey]
    );

    const hasProjectData = weeklyProjectData && weeklyProjectData.rows.length > 0;

    if (!hasProjectData) return null;

    return (
        <div className="info-popover-chart">
            {title && <p className="info-popover-chart-title">{title}</p>}
            <ResponsiveContainer width="100%" height={250}>
                <BarChart data={weeklyProjectData.rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={<AngledAxisTick />} height={60} />
                    <YAxis allowDecimals={false} axisLine={false} tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    {weeklyProjectData.projects.map((project, i) => (
                        <Bar
                            key={project}
                            dataKey={project}
                            stackId="results"
                            fill={WEEKLY_CHART_COLORS[i % WEEKLY_CHART_COLORS.length]}
                        stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <ChartDataTable table={weeklyTable(weeklyProjectData)} />
        </div>
    );
};

export default WeeklyResultsChart;
